import { SimpleSeriesVector, SmoothingDataInput } from '../types';

function gaussianKernel(sigma: number): Float64Array {
  const radius = Math.ceil(4 * sigma);
  const size = radius + 1; // We only generate weights for non-negative X values.
  const kernel = new Float64Array(size);
  const sigma2 = sigma * sigma;
  const factor = 1 / (Math.sqrt(2 * Math.PI) * sigma);

  for (let x = 0; x < size; x++) {
    const value = factor * Math.exp(-(x * x) / (2 * sigma2));
    kernel[x] = value;
  }

  // The sum of weights is not 1, but it's close enough.
  // We are going to normalize the weights when smoothing anyway.
  return kernel;
}

// Sigma for fullscreen chart with 100 smoothing at 4K resolution is
// 4000 / 2 / 4 = 500.
// We multiply by 4 to make sure interpolated weights are also accurate.
const SIGMA = 2000;

// Given that we have no guarantee that X axis has integer values,
// we can't assume that points will match neatly to the kernel indices.
// Instead we create this single reference table for the maximum sigma,
// and will use it to interpolate weights for any sigma value.
const KERNEL = gaussianKernel(SIGMA);

export function gaussianSmoothing(
  smoothingDataInput: SmoothingDataInput,
  // For Gaussian, the parameter is calculated so that weights of points inside
  // the window are 0.95 of total weight.
  smoothingWindowSize: number,
): SimpleSeriesVector[] {
  const { dataLength, seriesCount, getXValue, getBucket } = smoothingDataInput;

  const sigma = smoothingWindowSize / 4;

  const sigmaRatio = SIGMA / sigma;

  function getWeight(xOffset: number): number {
    const targetIndex = Math.abs(xOffset) * sigmaRatio;

    const leftIndex = Math.floor(targetIndex);
    const rightIndex = Math.ceil(targetIndex);

    const leftWeight = rightIndex - targetIndex;
    const rightWeight = 1 - leftWeight;

    return (KERNEL[leftIndex] ?? 0) * leftWeight + (KERNEL[rightIndex] ?? 0) * rightWeight;
  }

  const xValues = new Float64Array(dataLength);

  for (let i = 0; i < dataLength; i++) {
    xValues[i] = getXValue(i);
  }

  const inputYValues = new Array(seriesCount).fill(undefined).map((_, seriesIndex) => {
    const seriesValues = new Float64Array(dataLength);

    for (let i = 0; i < dataLength; i++) {
      const bucket = getBucket(seriesIndex, i);

      seriesValues[i] = bucket == null || bucket.count === 0 ? NaN : bucket.sum / bucket.count;
    }

    return seriesValues;
  });

  // Allocate result array. We will be computing values for each
  // series separately to improve data locality on reads, but
  // that means we need to have place ready to put them.
  const result: SimpleSeriesVector[] = Array.from(xValues, (x) => {
    const row = new Array<number | null>(seriesCount + 1).fill(null);

    row[0] = x;

    return row as SimpleSeriesVector;
  });

  const radius = 4 * sigma;

  for (let seriesIndex = 0; seriesIndex < seriesCount; seriesIndex++) {
    const seriesValues = inputYValues[seriesIndex];
    const outputSeriesIndex = seriesIndex + 1;

    let firstIdxInRadius = 0;
    let lastIdxInRadius = 0;

    for (let i = 0; i < dataLength; i++) {
      const x = xValues[i];

      const bucket = getBucket(seriesIndex, i);

      if (bucket == null || bucket.count === 0) {
        // Output is already filled with nulls, so we can skip this point.
        continue;
      }

      if (!Number.isFinite(bucket.sum)) {
        result[i][outputSeriesIndex] = bucket.sum;
        continue;
      }

      const windowStartX = x - radius;
      const windowEndX = x + radius;

      while (firstIdxInRadius < dataLength && getXValue(firstIdxInRadius) < windowStartX) {
        firstIdxInRadius++;
      }

      while (lastIdxInRadius < dataLength && getXValue(lastIdxInRadius) <= windowEndX) {
        lastIdxInRadius++;
      }

      let sum = 0;
      let weightsSum = 0;

      // This loop is quite expensive O(n * sigma), but it's a very simple implementation.
      //
      // An alternative would be to use FFT convolution, which would have better
      // complexity at least for large windows. This would come with other challenges
      // though, as it requires uniform data with consistent step and no gaps.
      //
      // FFT convolution would only work if we give up on using X axis values as reference.
      for (let j = firstIdxInRadius; j < lastIdxInRadius; j++) {
        const value = seriesValues[j];

        if (!Number.isFinite(value)) {
          continue;
        }

        const xOffset = getXValue(j) - x;
        const weight = getWeight(xOffset);

        sum += weight * value;
        weightsSum += weight;
      }

      result[i][outputSeriesIndex] = sum / weightsSum;
    }
  }

  return result;
}
